import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { QsgV1Model } from '../../fetch-data/graphql/types';
import { getAutocompleteOptions } from '../../utils/search/autocomplete';
import { Option } from '../../components/Input/types';

interface UseSearchAutocompleteProps {
  qsgContent: QsgV1Model | null;
  qsgSearchTerm: string;
  urlSearchTerm: string;
  limit?: number;
}

interface UseSearchAutocompleteReturn {
  searchInputValue: string;
  autocompleteOptions: Option[];
  handleSearchChange: (value: string) => void;
  qsgSearchOption: Option | null;
}

/**
 * Custom hook for managing search autocomplete functionality
 * Handles input state, autocomplete options, and syncing with URL/search state
 */
export const useSearchAutocomplete = ({
  qsgContent,
  qsgSearchTerm,
  urlSearchTerm,
  limit = 10,
}: UseSearchAutocompleteProps): UseSearchAutocompleteReturn => {
  const initialValue = urlSearchTerm || qsgSearchTerm || '';
  const [searchInputValue, setSearchInputValue] = useState(initialValue);

  // Track if user is actively typing to prevent sync from clearing input
  const isUserTypingRef = useRef(false);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncedValueRef = useRef<string>(initialValue);

  const autocompleteOptions = useMemo(() => {
    if (!qsgContent || !searchInputValue.trim()) {
      return [];
    }
    return getAutocompleteOptions(qsgContent, searchInputValue, limit);
  }, [qsgContent, searchInputValue, limit]);

  const handleSearchChange = useCallback((value: string) => {
    isUserTypingRef.current = true;
    setSearchInputValue(value);

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Reset typing flag after user stops typing for 1 second
    syncTimeoutRef.current = setTimeout(() => {
      isUserTypingRef.current = false;
    }, 1000);
  }, []);

  // Sync input value with URL/search term, but only if user is not actively typing
  useEffect(() => {
    // Don't sync if user is actively typing
    if (isUserTypingRef.current) return;

    const newValue = urlSearchTerm || qsgSearchTerm || '';

    // Only sync if:
    // 1. The external value actually changed (different from last synced value)
    // 2. AND it's different from current input value
    // 3. AND either the new value is not empty, OR the current input is empty
    const hasExternalValueChanged = newValue !== lastSyncedValueRef.current;
    const isDifferentFromInput = newValue !== searchInputValue;

    if (hasExternalValueChanged && isDifferentFromInput) {
      // Only update if new value is not empty, or if both are empty (clear case)
      if (newValue !== '' || searchInputValue === '') {
        setSearchInputValue(newValue);
        lastSyncedValueRef.current = newValue;
      }
    }
  }, [urlSearchTerm, qsgSearchTerm, searchInputValue]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  const qsgSearchOption = useMemo(() => {
    if (!qsgSearchTerm) return null;
    return { label: qsgSearchTerm, value: qsgSearchTerm };
  }, [qsgSearchTerm]);

  return {
    searchInputValue,
    autocompleteOptions,
    handleSearchChange,
    qsgSearchOption,
  };
};
